import { assert, logger } from '@fe/core';
import {
    CityPairItem,
    FlightLayerFacade,
    ImpactsLayerIds,
    ParkedFlight,
    ROUTE_FLOWN_POINTS_LAYER_ID,
    RouteFlownPointFeatureProperties
} from '@fe/flight-layer';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import { hasTooltip, WeatherPickingInfoLayer } from '@fe/weather';
import { GeoJsonProperties } from 'geojson';
import { Evented, MapGeoJSONFeature, Map as MaplibreMap, MapMouseEvent, PointLike, Subscription } from 'maplibre-gl';
import { EventBus } from '../app/contexts/event-bus';
import app from '../client/app';
import { DrawingObjectsLayer } from '../components/drawing-object/drawing-object-layer';
import { RouteAnalyzerLayer } from '../components/route-analyzer/layers/route-analyzer-layer';
import { PlannedScheduledFlightRecord } from '../flights/geojson-producer';
import { DIVERSION_LAYER_ID, DiversionAirportProperties } from '../layers/diversion-status-layer';
import { RVR_LAYER_ID, RvrFeatureProperties } from '../layers/runway-rvr-layer';
import { NavDataPlugin } from '../plugins/nav-data-plugin';
import { FeNavAirport } from '../static/nav-types';
import { MapTooltipSettings } from '../types/settings';
import { ImpactTooltipItem, MessageType } from './actor-messages';
import { FeWeatherPlugin } from './fe-weather-plugin';

const DEBOUNCE_MS = 15;

export type MapInteractionServiceEvents = {
    tooltip: { type: 'tooltip'; data: MapTooltipInfo | null };
};

export class MapInteractionService extends Evented<MapInteractionServiceEvents> implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private debounceTimer: number | null = null;
    private subscriptions: Subscription[] = [];
    private contextMenuActive = false;
    private enableTooltips = true;
    private enableContextMenu = true;
    private cursorIsPointer = false;
    private cursorInsideTooltip = false;
    private ctrlPressed = false;
    private lastMouseMoveEvent: MapMouseEvent | null = null;
    private settings: MapTooltipSettings | null = null;
    private lastTooltip: MapTooltipInfo | null = null;

    constructor(
        private readonly weatherService: FeWeatherPlugin,
        private readonly navDataFilter: NavDataPlugin,
        private readonly flightLayerFacade: FlightLayerFacade
    ) {
        super();
    }

    onAdd(map: MaplibreMap) {
        this.map = map;

        const keyUpOrDown = (e: KeyboardEvent) => {
            if (this.ctrlPressed !== e.ctrlKey) {
                this.ctrlPressed = e.ctrlKey;
                this.clearDebounce();
                if (this.lastMouseMoveEvent) {
                    this.handleMouseMove(this.lastMouseMoveEvent);
                }
            }
        };

        document.addEventListener('keydown', keyUpOrDown);
        document.addEventListener('keyup', keyUpOrDown);

        this.subscriptions = [
            this.map.on('mousemove', this.mouseMove),
            this.map.on('mouseout', this.mouseLeave),
            this.map.on('contextmenu', this.onContextMenu),
            {
                unsubscribe: () => {
                    document.removeEventListener('keydown', keyUpOrDown);
                    document.removeEventListener('keyup', keyUpOrDown);
                }
            }
        ];
    }

    onRemove() {
        this.subscriptions.forEach((x) => x.unsubscribe());
        this.subscriptions = [];
        this.clearDebounce();
        this.clearTooltip();
        this.clearContextMenu();
        this.lastMouseMoveEvent = null;
        this.map = null;
    }

    setTooltips(type: 'enabled' | 'disabled') {
        this.enableTooltips = type === 'enabled';
        this.cursorInsideTooltip = false;
        this.clearDebounce();
        if (!this.enableTooltips) {
            this.clearTooltip();
        }
    }

    setContextMenu(type: 'enabled' | 'disabled') {
        this.enableContextMenu = type === 'enabled';
        this.cursorInsideTooltip = false;
        this.clearDebounce();
        if (!this.enableContextMenu) {
            this.clearContextMenu();
        }
    }

    dismissTooltip() {
        this.cursorInsideTooltip = false;
        this.clearTooltip();
    }

    dismissContextMenu(source?: WindowProxy) {
        this.contextMenuActive = false;
        this.cursorInsideTooltip = false;
        this.clearContextMenu(source);
    }

    updateSettings(settings: MapTooltipSettings) {
        this.settings = settings;
    }

    private mouseMove = (e: MapMouseEvent) => {
        if (!this.enableTooltips || e.originalEvent.altKey) {
            return;
        }

        this.lastMouseMoveEvent = e;
        if (this.lastTooltip) {
            this.lastTooltip = {
                ...this.lastTooltip,
                event: e
            };
            this.fire('tooltip', { data: this.lastTooltip });
        }
        this.clearDebounce();
        this.debounceTimer = self.setTimeout(() => {
            this.handleMouseMove(e);
        }, DEBOUNCE_MS);
    };

    private mouseLeave = (e: MapMouseEvent) => {
        // Clear last event too
        this.lastMouseMoveEvent = null;

        if (e.originalEvent.relatedTarget) {
            const relative = e.originalEvent.relatedTarget as HTMLElement;
            if (isPartOfScrollableTooltip(relative)) {
                this.cursorInsideTooltip = true;
                return;
            }
        }

        this.clearDebounce();
        this.clearTooltip();
        this.resetCursor();
        // Clear context menu only when the user clicks outside the map
    };

    private onContextMenu = (e: MapMouseEvent) => {
        if (!this.enableContextMenu) {
            return;
        }

        this.clearDebounce();
        this.cursorInsideTooltip = false;
        this.handleContextMenu(e);
    };

    private async handleMouseMove(e: MapMouseEvent) {
        assert(this.map);

        if (this.cursorInsideTooltip || this.contextMenuActive) {
            return;
        }

        if (!this.map._camera.transform.isPointOnMapSurface(e.point)) {
            this.clearTooltip();
            this.resetCursor();
            return;
        }

        const tooltips = await this.getTooltips(e);
        const applyFilter = this.settings?.requireCtrlKey && !this.ctrlPressed;
        const filteredTooltips = applyFilter
            ? tooltips.filter((t) => {
                  // Skip those tooltips that require CTRL to be pressed
                  switch (t.kind) {
                      case 'weather':
                          return !this.settings?.items.weather;
                      case 'flight':
                      case 'parked-flight':
                      case 'pre-dep-flight':
                          return !this.settings?.items.planes;
                      case 'airport':
                      case 'navdata':
                          return !this.settings?.items.navData;
                      default:
                          return !this.settings?.items.other;
                  }
              })
            : tooltips;

        if (this.cursorIsPointer) {
            this.clearTooltip();
        }

        if (filteredTooltips.length !== 0) {
            this.setCursorPointer();
            const tooltip: MapTooltipInfo = {
                event: e,
                tooltips: filteredTooltips
            };
            this.lastTooltip = tooltip;
            this.fire('tooltip', { data: tooltip });
        } else {
            // We got nothing
            this.clearTooltip();
        }
    }

    private async handleContextMenu(e: MapMouseEvent) {
        assert(this.map);
        const elements = await this.getContextMenuElements(e);
        if (elements.length) {
            EventBus.fireContextMenu(elements, e.originalEvent);
            this.contextMenuActive = true;
            this.clearTooltip();
        }
    }

    private async getContextMenuElements(e: MapMouseEvent): Promise<MapContextMenuData[]> {
        assert(this.map);
        const data = [] as MapContextMenuData[];
        const flights = this.flightLayerFacade.deckPlugin?.queryFlights([e.point.x, e.point.y]);
        if (flights && flights.length > 0) {
            data.push({
                kind: 'flight',
                flightIds: flights
            });
        }

        const preDepFlights = this.flightLayerFacade.deckPlugin?.queryPreDepartureFlights([e.point.x, e.point.y]);
        if (preDepFlights && (preDepFlights.planned.length > 0 || preDepFlights.scheduled.length > 0)) {
            data.push({
                kind: 'pre-dep-flight',
                flights: preDepFlights
            });
        }

        const airports = this.map!.queryRenderedFeatures(e.point, { layers: ['airport'] });
        if (airports.length > 0) {
            const airportId = airports[0].properties.icaoid ?? airports[0].properties.faaid ?? airports[0].properties.iataid;
            this.contextMenuActive = true;
            data.push({
                kind: 'airport',
                airportId: airportId
            });
        }

        const width = 10;
        const height = 10;
        const bounds: [PointLike, PointLike] = [
            [e.point.x - width / 2, e.point.y - height / 2],
            [e.point.x + width / 2, e.point.y + height / 2]
        ];

        const routeAnalyzerFeatures = this.map.queryRenderedFeatures(bounds, {
            layers: [RouteAnalyzerLayer.CONTEXT_MENU_LAYER]
        });

        if (routeAnalyzerFeatures.length > 0) {
            data.push({
                kind: 'route-analyzer',
                routes: routeAnalyzerFeatures.map((x) => x.properties.id).filter((x) => x)
            });
        }

        const weatherItems = await this.weatherService.mapLayerStack?.pickObjects(e);
        if (weatherItems && weatherItems.length > 0) {
            data.push({ kind: 'weather', layers: weatherItems });
        }

        const navData = this.navDataFilter.queryContextMenuInfo(e, this.map);
        if (navData && navData.length > 0) {
            data.push({ kind: 'navdata', items: navData });
        }

        const drawingObjectFeatures = this.map.queryRenderedFeatures(bounds, {
            layers: [DrawingObjectsLayer.CONTEXT_MENU_LAYER]
        });

        if (drawingObjectFeatures.length > 0) {
            data.push({ kind: 'drawing-object', objectIds: drawingObjectFeatures.map((x) => x.properties.id) });
        }

        return data;
    }

    private async getTooltips(e: MapMouseEvent): Promise<MapTooltipData[]> {
        const tooltips = [] as MapTooltipData[];
        // TODO: call query rendered features once and then filter, no need to call individually for each thing

        const airports = this.map!.queryRenderedFeatures(e.point, { layers: ['airport'] });
        if (airports.length > 0) {
            tooltips.push({
                kind: 'airport',
                airport: airports[0].properties as FeNavAirport
            });
        }

        const flights = this.flightLayerFacade.deckPlugin?.queryFlights([e.point.x, e.point.y]);
        if (flights && flights.length > 0) {
            tooltips.push({
                kind: 'flight',
                id: flights[0]
            });
        }

        const preDepFlights = this.flightLayerFacade.deckPlugin?.queryPreDepartureFlights([e.point.x, e.point.y]);
        if (preDepFlights && (preDepFlights.planned.length > 0 || preDepFlights.scheduled.length > 0)) {
            tooltips.push({
                kind: 'pre-dep-flight',
                flights: preDepFlights
            });
        }

        const parkedFlights = this.flightLayerFacade.deckPlugin.queryParkedFlights([e.point.x, e.point.y]);
        if (parkedFlights.length > 0) {
            tooltips.push({
                kind: 'parked-flight',
                flights: parkedFlights
            });
        }

        const cityPair = this.flightLayerFacade.deckPlugin.queryCityPair([e.point.x, e.point.y]);
        if (cityPair.length > 0) {
            tooltips.push({
                kind: 'city-pair',
                items: cityPair
            });
        }

        // Weather tooltip
        const weather = await this.weatherService.mapLayerStack?.pickObjects(e);
        const filteredLayers = weather
            ?.map((layer) => ({
                ...layer,
                objects: layer.objects.filter((object) => hasTooltip(layer, object))
            }))
            .filter((x) => x.objects.length > 0);

        if (filteredLayers && filteredLayers.length > 0) {
            tooltips.push({
                kind: 'weather',
                layers: filteredLayers
            });
        }

        // navData tooltip
        const navData = this.navDataFilter.queryHoverInfo(e);
        if (navData && navData.length > 0) {
            tooltips.push({
                kind: 'navdata',
                items: navData
            });
        }

        if (this.map!.getLayer(DIVERSION_LAYER_ID)) {
            const diversion = this.map!.queryRenderedFeatures(e.point, { layers: [DIVERSION_LAYER_ID] });
            if (diversion.length > 0) {
                tooltips.push({
                    kind: 'diversion',
                    items: diversion.map((item) => item.properties as DiversionAirportProperties)
                });
            }
        }

        if (this.map!.getLayer(RVR_LAYER_ID)) {
            const rvr = this.map!.queryRenderedFeatures(e.point, { layers: [RVR_LAYER_ID] });
            if (rvr.length > 0) {
                tooltips.push({
                    kind: 'rvr',
                    items: rvr.map((item) => item.properties as RvrFeatureProperties)
                });
            }
        }

        const flightImpactsGuidMap = new Map<number, string[]>();
        const impactFeatures: MapGeoJSONFeature[] = [];

        try {
            const features = this.map!.queryRenderedFeatures(e.point, {
                layers: ImpactsLayerIds
            });
            features.forEach((feature) => {
                if (
                    feature.properties &&
                    Object.prototype.hasOwnProperty.call(feature.properties, 'impactGuid') &&
                    Object.prototype.hasOwnProperty.call(feature.properties, 'recordId')
                ) {
                    impactFeatures.push(feature);
                }
            });
        } catch (e) {
            logger.error(e);
        }

        impactFeatures.forEach((feature) => {
            const impactGuid = feature.properties?.impactGuid;
            const flightId = feature.properties?.recordId;

            if (impactGuid && flightId) {
                const guids = flightImpactsGuidMap.get(flightId) ?? [];
                guids.push(impactGuid);
                flightImpactsGuidMap.set(flightId, guids);
            }
        });
        const impacts = await app.worker.actor.sendAsync({
            type: MessageType.getImpactsByIds,
            data: flightImpactsGuidMap
        });

        if (impacts.length > 0) {
            tooltips.push({
                kind: 'impacts',
                items: impacts
            });
        }

        const flownPoints = this.map?.queryRenderedFeatures(
            [
                [e.point.x - 6 / 2, e.point.y - 6 / 2],
                [e.point.x + 6 / 2, e.point.y + 6 / 2]
            ],
            {
                layers: [ROUTE_FLOWN_POINTS_LAYER_ID],
                filter: ['==', ['geometry-type'], 'Point']
            }
        );

        if (flownPoints?.length) {
            tooltips.push({
                items: flownPoints.map((x) => x.properties as RouteFlownPointFeatureProperties),
                kind: 'flown-point'
            });
        }
        return tooltips;
    }

    private clearTooltip() {
        this.lastTooltip = null;
        this.fire('tooltip', { data: null });
        this.resetCursor();
    }

    private clearContextMenu(source?: WindowProxy) {
        EventBus.fire('context-menu', { view: source, elements: [] });
    }

    private clearDebounce() {
        if (this.debounceTimer) {
            self.clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    private setCursorPointer() {
        if (this.map && !this.cursorIsPointer) {
            this.map.getCanvas().style.cursor = 'pointer';
            this.cursorIsPointer = true;
        }
    }

    private resetCursor() {
        if (this.map && this.cursorIsPointer) {
            this.map.getCanvas().style.cursor = '';
            this.cursorIsPointer = false;
        }
    }
}

export type TooltipData<TName extends string, TObject extends object> = { kind: TName } & TObject;
export type AirportTooltipData = TooltipData<'airport', { airport: FeNavAirport }>;
export type FlightTooltipData = TooltipData<'flight', { id: number }>;
export type WeatherTooltipData = TooltipData<'weather', { layers: WeatherPickingInfoLayer[] }>;
export type NavDataTooltipData = TooltipData<'navdata', { items: { layerName: string; properties: GeoJsonProperties }[] }>;
export type ImpactsTooltipData = TooltipData<'impacts', { items: ImpactTooltipItem[] }>;
export type RouteFlownPointTooltipData = TooltipData<'flown-point', { items: RouteFlownPointFeatureProperties[] }>;
export type DiversionTooltipData = TooltipData<'diversion', { items: DiversionAirportProperties[] }>;
export type RvrTooltipData = TooltipData<'rvr', { items: RvrFeatureProperties[] }>;
export type CityPairTooltipData = TooltipData<'city-pair', { items: CityPairItem[] }>;
export type FlightPreDepTooltipData = TooltipData<
    'pre-dep-flight',
    {
        flights: {
            planned: Array<{ airport: string; items: PlannedScheduledFlightRecord[] }>;
            scheduled: Array<{ airport: string; items: PlannedScheduledFlightRecord[] }>;
        };
    }
>;
export type FlightParkedTooltipData = TooltipData<'parked-flight', { flights: ParkedFlight[] }>;
export type NoTooltipInfo = null;

export type MapTooltipData =
    | FlightTooltipData
    | FlightPreDepTooltipData
    | FlightParkedTooltipData
    | AirportTooltipData
    | WeatherTooltipData
    | NavDataTooltipData
    | DiversionTooltipData
    | RouteFlownPointTooltipData
    | ImpactsTooltipData
    | RvrTooltipData
    | CityPairTooltipData;

export type MapTooltipInfo =
    | {
          event: MapMouseEvent;
          tooltips: MapTooltipData[];
      }
    | NoTooltipInfo;

// Context menus
export type ContextMenuData<TName extends string, TObject extends object> = { kind: TName } & TObject;
export type AirportContextMenuData = ContextMenuData<'airport', { airportId: string /* TODO: convert to list: do we need to?*/ }>;
export type ActiveFlightContextMenuData = ContextMenuData<'flight', { flightIds: number[] }>;
export type WeatherContextMenuData = ContextMenuData<'weather', { layers: WeatherPickingInfoLayer[] }>;
export type NavDataContextMenuData = ContextMenuData<'navdata', { items: NavDataContextMenuContent[] }>;
export type RouteAnalyzerContextMenuData = ContextMenuData<'route-analyzer', { routes: string[] }>;
export type DrawingObjectContextMenuData = ContextMenuData<'drawing-object', { objectIds: string[] }>;
export type FlightPreDepContextMenuData = ContextMenuData<
    'pre-dep-flight',
    {
        flights: {
            planned: Array<{ airport: string; items: PlannedScheduledFlightRecord[] }>;
            scheduled: Array<{ airport: string; items: PlannedScheduledFlightRecord[] }>;
        };
    }
>;
export type NoContextMenu = [];
export type MapContextMenuData =
    | ActiveFlightContextMenuData
    | AirportContextMenuData
    | WeatherContextMenuData
    | NavDataContextMenuData
    | RouteAnalyzerContextMenuData
    | DrawingObjectContextMenuData
    | FlightPreDepContextMenuData;

export type MapContextMenuInfo =
    | {
          event: MouseEvent;
          elements: MapContextMenuData[];
          view?: WindowProxy | null;
      }
    | {
          elements: [];
          view?: WindowProxy | null;
      };

export interface NavDataContextMenuContent {
    layerName: string;
    properties: GeoJsonProperties;
    geometry: GeoJSON.Geometry;
}

function isPartOfScrollableTooltip(node: HTMLElement | null) {
    while (node) {
        if (node.classList.contains('map-tooltip') && node.getAttribute('data-scrollable') === 'true') {
            return true;
        }
        node = node.parentElement;
    }
    return false;
}
